/**
 * 座位規劃相關 API
 */

import { POLLING } from '@/config/constants';
import { apiClient, ApiError } from './client';
import type { PlannerResponse, TaskResponse } from '@/types/api';
import type { Door } from '@/types';

// ========================================================================
// 型別定義
// ========================================================================

import type { DeskCountMode, FurnitureOrderItem } from '@/types/api';

/** 規劃請求參數 */
export interface PlanFurnitureParams {
  image: File;
  doors?: Door[];
  /** 快速規劃模式：只產生一個方案 */
  quickPlan?: boolean;
  settings: {
    deskWidth: number;
    deskHeight: number;
    chairDepth: number;
    peopleDensity: number;
    // 辦公桌數量設定
    deskCountMode: DeskCountMode;
    customDeskCount?: number;
    // 家具擺放順序
    furnitureOrder: FurnitureOrderItem[];
    // 其他設定
    referenceLineLength?: number;
    referenceLineUnit?: string;
    windowDirection?: string;
    supervisorRoomCount: number;
    supervisorRoomWidth: number;
    supervisorRoomDepth: number;
    largeMeetingRoomCount: number;
    largeMeetingRoomWidth: number;
    largeMeetingRoomDepth: number;
    mediumMeetingRoomCount: number;
    mediumMeetingRoomWidth: number;
    mediumMeetingRoomDepth: number;
    smallMeetingRoomCount: number;
    smallMeetingRoomWidth: number;
    smallMeetingRoomDepth: number;
    mainDoorClearance: number;
    normalDoorClearance: number;
    emergencyDoorClearance: number;
  };
  referenceLine?: {
    startX: number;
    startY: number;
    endX: number;
    endY: number;
    lengthMeters: number;
  };
  detectedDimensions?: Array<{
    value: number;
    unit: string;
    position: { x: number; y: number };
    orientation: string;
  }>;
}

/** 進度回呼函式 */
export type ProgressCallback = (progress: number, message: string) => void;

// ========================================================================
// 同步規劃 API
// ========================================================================

/**
 * 執行座位規劃（使用非同步 API 並等待結果）
 */
export async function planFurniture(params: PlanFurnitureParams): Promise<PlannerResponse> {
  // 啟動非同步任務
  const jobId = await startAsyncPlanning(params);

  // 輪詢等待結果
  const maxAttempts = 300; // 最多等待 5 分鐘（1秒一次）
  for (let i = 0; i < maxAttempts; i++) {
    const status = await getTaskStatus(jobId);

    if (status.status === 'completed') {
      // 任務完成，獲取結果
      return await getJobResult(jobId);
    }

    if (status.status === 'failed') {
      throw new ApiError(500, status.error || '規劃失敗');
    }

    if (status.status === 'cancelled') {
      throw new ApiError(499, '規劃已取消');
    }

    // 等待 1 秒後再次查詢
    await new Promise((resolve) => setTimeout(resolve, 1000));
  }

  throw new ApiError(408, '規劃超時');
}

// ========================================================================
// 非同步規劃 API
// ========================================================================

/**
 * 啟動非同步座位規劃
 */
export async function startAsyncPlanning(params: PlanFurnitureParams): Promise<string> {
  const formData = createPlannerFormData(params);

  const response = await apiClient.postForm<{ job_id: string }>('/api/v1/plan-furniture-async', {
    body: formData,
  });

  return response.job_id;
}

/** 任務狀態回應 */
interface JobStatusResponse {
  job_id: string;
  status: string;
  progress: {
    current: number;
    total: number;
    percentage: number;
  };
  error?: string;
}

/**
 * 查詢任務狀態
 */
export async function getTaskStatus(jobId: string): Promise<TaskResponse> {
  const response = await apiClient.get<JobStatusResponse>(`/api/v1/jobs/${jobId}/status`);

  return {
    task_id: response.job_id,
    status: response.status as TaskResponse['status'],
    progress: response.progress.percentage,
    error: response.error,
  };
}

/**
 * 取得任務結果
 */
export async function getJobResult(jobId: string): Promise<PlannerResponse> {
  return apiClient.get<PlannerResponse>(`/api/v1/jobs/${jobId}/result`);
}

/**
 * 執行非同步規劃並等待結果
 */
export async function planFurnitureAsync(
  params: PlanFurnitureParams,
  onProgress?: ProgressCallback
): Promise<PlannerResponse> {
  // 啟動非同步任務
  onProgress?.(0, '提交規劃請求...');
  const taskId = await startAsyncPlanning(params);

  // 輪詢等待結果
  const startTime = Date.now();
  let lastProgress = 0;

  while (true) {
    // 檢查超時
    if (Date.now() - startTime > POLLING.TIMEOUT) {
      throw new ApiError(408, '規劃超時，請稍後再試');
    }

    // 查詢狀態
    const status = await getTaskStatus(taskId);

    // 更新進度
    const progress = status.progress ?? lastProgress;
    if (progress !== lastProgress) {
      lastProgress = progress;
      onProgress?.(progress, status.message ?? '處理中...');
    }

    // 檢查結果
    switch (status.status) {
      case 'completed':
        onProgress?.(100, '規劃完成');
        return status.result as PlannerResponse;

      case 'failed':
        throw new ApiError(500, status.error ?? '規劃失敗');

      case 'pending':
      case 'processing':
        // 繼續等待
        await sleep(POLLING.INTERVAL);
        break;
    }
  }
}

// ========================================================================
// 輔助函式
// ========================================================================

/**
 * 建立規劃用的 FormData
 * 後端 API 期望個別的 Form 欄位，而非 JSON 物件
 */
function createPlannerFormData(params: PlanFurnitureParams): FormData {
  const formData = new FormData();
  const { settings, referenceLine } = params;

  // 圖片
  formData.append('image', params.image);

  // 參考線（必填）
  if (referenceLine) {
    // 計算參考線像素長度
    const dx = referenceLine.endX - referenceLine.startX;
    const dy = referenceLine.endY - referenceLine.startY;
    const pixelLength = Math.sqrt(dx * dx + dy * dy);
    formData.append('reference_length_pixels', String(pixelLength));
    formData.append('reference_length_meters', String(referenceLine.lengthMeters));
  } else {
    // 使用預設值（避免驗證錯誤）
    formData.append('reference_length_pixels', '100');
    formData.append('reference_length_meters', '1');
  }

  // 辦公桌設定（UI 已使用公尺，直接傳送）
  formData.append('desk_width', String(settings.deskWidth));
  formData.append('desk_height', String(settings.deskHeight));
  formData.append('chair_depth', String(settings.chairDepth));
  formData.append('people_density', String(settings.peopleDensity));

  // 辦公桌數量設定
  formData.append('desk_count_mode', settings.deskCountMode);

  // 傳送 use_system_desk_count 參數：只有 'auto' 模式才使用系統建議桌數
  // 後端預設值為 true，若不傳送此參數會導致 max 模式也使用系統建議桌數
  formData.append('use_system_desk_count', String(settings.deskCountMode === 'auto'));

  if (settings.deskCountMode === 'max') {
    // max 模式：傳送 -1 表示計算最大桌數
    formData.append('custom_desk_count', '-1');
  } else if (settings.deskCountMode === 'custom' && settings.customDeskCount !== undefined) {
    // custom 模式：傳送指定桌數
    formData.append('custom_desk_count', String(settings.customDeskCount));
  }
  // auto 模式：不傳送 custom_desk_count，後端會根據 people_density 計算

  // 家具擺放順序（後端期望收到 label 的字串陣列，如 ["主管室", "大會議室", ...]）
  if (settings.furnitureOrder && settings.furnitureOrder.length > 0) {
    const orderLabels = settings.furnitureOrder.map(item => item.label);
    formData.append('furniture_order', JSON.stringify(orderLabels));
  }

  // 窗戶方向
  if (settings.windowDirection) {
    formData.append('window_direction', settings.windowDirection);
  }

  // 主管室設定（UI 已使用公尺，直接傳送）
  formData.append('supervisor_room_count', String(settings.supervisorRoomCount));
  formData.append('supervisor_room_width', String(settings.supervisorRoomWidth));
  formData.append('supervisor_room_height', String(settings.supervisorRoomDepth));

  // 大會議室設定（UI 已使用公尺，直接傳送）
  formData.append('large_meeting_count', String(settings.largeMeetingRoomCount));
  formData.append('large_meeting_width', String(settings.largeMeetingRoomWidth));
  formData.append('large_meeting_height', String(settings.largeMeetingRoomDepth));

  // 中會議室設定（UI 已使用公尺，直接傳送）
  formData.append('medium_meeting_count', String(settings.mediumMeetingRoomCount));
  formData.append('medium_meeting_width', String(settings.mediumMeetingRoomWidth));
  formData.append('medium_meeting_height', String(settings.mediumMeetingRoomDepth));

  // 小會議室設定（UI 已使用公尺，直接傳送）
  formData.append('small_meeting_count', String(settings.smallMeetingRoomCount));
  formData.append('small_meeting_width', String(settings.smallMeetingRoomWidth));
  formData.append('small_meeting_height', String(settings.smallMeetingRoomDepth));

  // 門標註
  if (params.doors && params.doors.length > 0) {
    formData.append('doors', JSON.stringify(params.doors));
  }

  // 門口淨空距離（使用主要入口的值）
  formData.append('door_clearance_distance', String(settings.mainDoorClearance));

  // 快速規劃模式：跳過尺寸組合，只產生一個方案
  if (params.quickPlan) {
    formData.append('skip_dimension_combinations', 'true');
  }

  return formData;
}

/**
 * 延遲函式
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
